import WebSocketService from '@/services/WebSocketService';

/**
 * Vue mixin to easily connect to a WebSocket in a component
 * Once you loaded the mixin, you can use it by defining a `socket` option on the component
 *
 * socket: {
 *   namespace: '/api/heartbeat/',
 *   // Conditionnally connect/disconnect the Websocket based on the skip function return value
 *   skip() {
 *     return false;
 *   },
 *   events: {
 *     message(event) {
 *       const data = JSON.parse(event.data);
 *       console.log(data);
 *     },
 *     error(error) {
 *       // Handle error
 *     },
 *     ...
 *   }
 * }
 *
 * Use `this.$socket.send({ message: 'hello' });` to send a message to the server.
 * Be careful as it will work only when the websocket is open.
 */

export default {
  created() {
    const options = this.$options.socket;

    if (!options) return;

    if (options.skip) {
      if (typeof options.skip !== 'function') {
        throw new Error('Socket `skip` parameter must be a function.');
      }

      this._skipWatcher = this.$watch(
        options.skip.bind(this),
        shouldSkip => {
          if (shouldSkip) {
            this._stopWebSocket(options);
          } else {
            this._startWebSocket(options);
          }
        },
        {
          immediate: true,
        }
      );
    } else {
      this._startWebSocket(options);
    }
  },

  beforeDestroy() {
    const options = this.$options.socket;

    this._stopWebSocket(options);

    if (this._skipWatcher) {
      this._skipWatcher();
    }
  },

  methods: {
    _startWebSocket(options) {
      if (!options.namespace) return;

      this.$socket = new WebSocketService(options);

      // Handlers are registered on the service (not on the raw `ws`) so they survive
      // reconnections: the service re-attaches them to every new underlying WebSocket.
      if (options.events) {
        const handlers = {};
        Object.keys(options.events).forEach(key => {
          handlers[key] = options.events[key].bind(this);
        });
        this.$socket.setEventHandlers(handlers);
      }

      this.$socket.connect();
    },

    _stopWebSocket() {
      if (!this.$socket) return;

      this.$socket.disconnect();
      delete this.$socket;
    },
  },
};
